import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { FormEvent, KeyboardEvent, MouseEvent, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import SurpriseButton, { SurpriseTextsType } from "../../../../../../components/common/SurpriseButton/SurpriseButton";
import styles from "./TgPromptInput.module.scss";

type Props = {
	prefix?: string;
	placeholder?: string;
	prompt?: string;
	onChange?: (newPrompt: string) => void;
	onEnter?: (e: KeyboardEvent<HTMLSpanElement>) => void;
	surpriseTextsList?: SurpriseTextsType;
	disabled?: boolean;
	className?: string;
	fontSize?: string;
};

const TgPromptInput = ({
	prefix = "",
	prompt = "",
	placeholder = "",
	onChange,
	onEnter,
	surpriseTextsList,
	disabled,
	className,
	fontSize,
}: Props) => {
	const { t } = useTranslation("translation", { keyPrefix: "newTg.app.promptInput" });
	const inputRef = useRef<HTMLSpanElement>(null);

	const moveCaretToEnd = () => {
		if (!inputRef.current) return;
		const range = document.createRange();
		range.selectNodeContents(inputRef.current);
		range.collapse(false);
		const sel = window.getSelection();
		sel?.removeAllRanges();
		sel?.addRange(range);
	};

	useEffect(() => {
		if (!inputRef.current) return;

		if (disabled) {
			inputRef.current.innerText = prompt || "";
			return;
		}

		if (prompt && prompt.trim()) {
			inputRef.current.innerText = prompt;
		} else {
			inputRef.current.innerText = " ";
		}

		moveCaretToEnd();
	}, [prompt, disabled]);

	const handleFocus = () => {
		if (disabled) return;
		inputRef.current?.focus();
		moveCaretToEnd();
	};

	const handleInput = (e: FormEvent<HTMLSpanElement>) => {
		if (disabled) return;
		const rawText = e.currentTarget.innerText;
		onChange?.(rawText.trim());
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLSpanElement>) => {
		if (disabled) return;
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			onEnter?.(e);
		}
	};

	const handleSurpriseClick = (_e: MouseEvent<HTMLButtonElement>, surprisedText: string) => {
		onChange?.(surprisedText);
		if (inputRef.current) {
			inputRef.current.innerText = surprisedText || " ";
			inputRef.current.focus();
			moveCaretToEnd();
		}
	};

	const handleBlur = () => {
		if (!inputRef.current || disabled) return;
		if (inputRef.current.innerText.trim() === "") {
			inputRef.current.innerText = " ";
		}
	};

	return (
		<Box className={clsx(styles.inputWrapper, { [styles.disabled]: disabled }, className)} onClick={handleFocus}>
			<Box className={styles.prompt}>
				<span className={styles.prefixLine}>
					{!disabled && (
						<span className={styles.prefixTag} style={{ fontSize }}>
							{t("prefix.tag")}
						</span>
					)}
					{prefix ? (
						<span className={styles.prefixText} style={{ fontSize }}>
							{prefix}
						</span>
					) : (
						<span className={styles.inputPrompt} style={{ fontSize }} data-placeholder={placeholder} />
					)}
					<span
						ref={inputRef}
						className={styles.inputPrompt}
						style={{ fontSize }}
						contentEditable={!disabled}
						tabIndex={disabled ? -1 : 0}
						suppressContentEditableWarning
						onInput={handleInput}
						onKeyDown={handleKeyDown}
						onBlur={handleBlur}
						dir="ltr"
					/>
				</span>
			</Box>

			{surpriseTextsList && (
				<Box className={styles.footer}>
					<Typography className={styles.footerText}>{t("footer")}</Typography>
					<SurpriseButton
						className={styles.surpriseBtn}
						buttonType="textSmall"
						textsListType={surpriseTextsList}
						onSurprise={handleSurpriseClick}
					/>
				</Box>
			)}
		</Box>
	);
};

export default observer(TgPromptInput);
